import { useCallback, useEffect, useMemo, useState } from "react";
import { routeParam } from "../lib/route";
import "./BoardPage.css";
import { AppShell, SideAction, SideGroup, SideNote } from "../app/AppShell";
import { Button } from "../components/Button";
import { Alert, EmptyState, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Input, Select } from "../components/Field";
import { IssueCard, complexityText, minutesSince, priorityText } from "../components/IssueCard";
import { Chip, StatusPill } from "../components/Status";
import type { StatusKey } from "../components/Status";
import type { Issue, Milestone } from "../lib/tasks";
import { errText, isHeld, isMerging, isWorking, tasksApi } from "../lib/tasks";
import { hole, rich, tx } from "../i18n";
import { BoardSelBar } from "../components/BoardSelBar";
import { Icon } from "../components/Icon";
import type { Project } from "../lib/project";
import { projectApi } from "../lib/project";
import { IssueDetailModal } from "../components/IssueDetail";

/* ══ 이슈 보드 · 목록 · 상세 ══
   채택 시안(Figma node 13:2) 그대로다. **`GET /tasks`·`GET /milestones` 를 읽고, 끌어
   옮기기는 `POST /tasks/:id/status`·`POST /tasks/:id/queue-order` 로 간다** — 새 API 를
   만들지 않고 M5 가 세운 문에 배선만 한다(t-in-web-api-wire).

   이 화면이 요구하는 값 목록은 `docs/screens/issue-screens-data.md` 에 있다. */

/** 열은 다섯이다(사람 확정 — 안 A). **병합 열을 만들지 않는다**: 병합 대기는 검토 열
 *  안의 카드 표식이고, 그것이 b3x-pr 의 「병합은 파생 컬럼」 원칙이다.
 *
 *  `blocked` 는 자기 열이 없다. 대기 열에 서고 카드의 상태 칩이 「✋ 막힘」으로 정확도를
 *  가진다 — 다섯 열이 무엇이 어디쯤 왔나를 말하고, 칩이 정확히 무엇인지를 말한다.
 *  열을 여섯으로 늘리면 시안이 고른 단순함이 무너진다. */
const COLS: { id: string; label: string; has: StatusKey[]; drop: StatusKey }[] = [
  { id: "todo", label: tx("issue.status.todo"), has: ["todo"], drop: "todo" },
  { id: "ready", label: tx("issue.status.ready"), has: ["ready", "blocked"], drop: "ready" },
  { id: "in-progress", label: tx("issue.status.inProgress"), has: ["in-progress"], drop: "in-progress" },
  { id: "review", label: tx("issue.status.review"), has: ["review"], drop: "review" },
  { id: "done", label: tx("issue.status.done"), has: ["done", "cancelled"], drop: "done" },
];

type SortKey = "queue" | "updated" | "created" | "title" | "priority";
/** 목록 보기에서 한 번에 그리는 열린 이슈 수 — 넘치면 「n건 더 보기」. */
const LIST_PAGE = 100;
const isClosed = (t: Issue) => t.status === "done" || t.status === "cancelled";

/** 칸 접기는 사람마다의 편의라 브라우저에만 둔다. 못 읽으면(사생활 창 등) 다 펼친 채로 그린다. */
const FOLD_KEY = "b3xin.board.fold";
function readFold(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(FOLD_KEY) ?? "[]");
    return new Set(Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

/** 한 칸의 줄 — 카드 하나, 또는 **같은 에픽의 하위가 이 칸에 둘 이상** 모인 묶음 상자.
 *  줄 순서는 지키고(묶음은 첫 하위가 선 자리에 선다), 부모 행 자체는 묶지 않는다 — 부모는
 *  하위에서 파생된 자기 단계의 칸에 따로 선다. */
type ColItem = { kind: "card"; t: Issue } | { kind: "epic"; parentId: string; kids: Issue[] };
function groupCol(cards: Issue[]): ColItem[] {
  const count = new Map<string, number>();
  for (const t of cards) if (t.parentId) count.set(t.parentId, (count.get(t.parentId) ?? 0) + 1);
  const out: ColItem[] = [];
  const box = new Map<string, Issue[]>();
  for (const t of cards) {
    if (t.parentId && (count.get(t.parentId) ?? 0) > 1) {
      const kids = box.get(t.parentId);
      if (kids) kids.push(t);
      else {
        const k = [t];
        box.set(t.parentId, k);
        out.push({ kind: "epic", parentId: t.parentId, kids: k });
      }
    } else out.push({ kind: "card", t });
  }
  return out;
}
const PRIORITY_ORDER = { critical: 0, high: 1, normal: 2 } as const;
/** 줄에 아직 안 선 이슈(`queueRank === null`)는 정렬에서 맨 뒤로 — 없는 값을 0 으로 읽지 않는다. */
const rankOf = (t: Issue) => t.queueRank ?? Number.POSITIVE_INFINITY;

export function BoardPage() {
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<"board" | "list">("board");
  const [q, setQ] = useState("");
  const [milestone, setMilestone] = useState("");
  const [epicFilter, setEpicFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("queue");
  const [asc, setAsc] = useState(true);
  // 프로젝트 거르개 — "" 전부 · "-" 미분류 · 그 밖은 프로젝트 id.
  const [project, setProject] = useState("");
  // null = 아직 · false = 못 읽음(보드는 그대로 그리고 레일에 그 사실만 적는다).
  const [projects, setProjects] = useState<Project[] | null | false>(null);
  const [listShown, setListShown] = useState(LIST_PAGE);
  const [closedOpen, setClosedOpen] = useState(false);
  const [folded, setFolded] = useState<Set<string>>(readFold);
  // 접은 에픽 묶음 — 「칸|부모」 열쇠. 화면 상태다.
  const [shut, setShut] = useState<Set<string>>(() => new Set());
  const toggleFold = (id: string) =>
    setFolded((f) => {
      const n = new Set(f);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      try {
        localStorage.setItem(FOLD_KEY, JSON.stringify([...n]));
      } catch {
        /* 저장 못 하면 이번 화면에서만 접힌다 — 다음에 다시 펼쳐 그려도 잃는 것이 없다 */
      }
      return n;
    });
  // 상세는 화면 상태지만 **주소로도 열린다**(`#/board?open=<id>`) — 알람이 이슈 상세로 가는 길이다.
  const [openId, setOpenId] = useState<string | null>(() => routeParam("open"));
  const [drag, setDrag] = useState<string | null>(null);
  // 보드 선택 줄 — 고른 카드 id. 화면 상태다(주소에 안 싣는다).
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const togglePick = (id: string, on: boolean) =>
    setPicked((p) => {
      const n = new Set(p);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const load = useCallback(async () => {
    try {
      const [ts, ms] = await Promise.all([tasksApi.list(), tasksApi.milestones()]);
      setIssues(ts);
      setMilestones(ms);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errText(e));
    }
  }, []);

  useEffect(() => {
    load();
    projectApi.list().then(setProjects, () => setProjects(false));
  }, [load]);

  /* 이미 보드에 있을 때 알람을 누르면 경로는 그대로라 화면이 다시 안 그려진다 — 주소의 `open` 을
     직접 듣는다. 닫을 때는 주소에서 값을 걷어 낸다(`replaceState` — 이벤트를 안 낸다). 안 걷으면
     같은 알람을 다시 눌러도 주소가 안 바뀌어 상세가 안 열린다. */
  useEffect(() => {
    const on = () => {
      const id = routeParam("open");
      if (id) setOpenId(id);
    };
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  const closeDetail = useCallback(() => {
    setOpenId(null);
    if (routeParam("open")) history.replaceState(null, "", "#/board");
  }, []);

  const milestoneTitle = useMemo(() => {
    const m = new Map(milestones.map((x) => [x.id, x.title]));
    return (id: string | null) => (id ? (m.get(id) ?? id) : undefined);
  }, [milestones]);

  const list = issues ?? [];
  const epicTitle = (id: string | null) => list.find((t) => t.id === id)?.title;
  const open = openId ? (list.find((t) => t.id === openId) ?? null) : null;
  /** 주소로 받은 id 가 목록에 없다 — 지워졌거나 못 읽은 이슈다. 조용히 아무것도 안 열면 사람은
   *  눌렀는데 안 됐다고 읽는다. */
  const missing = issues !== null && openId !== null && open === null ? openId : null;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((t) => {
      if (epicFilter && t.parentId !== epicFilter && t.id !== epicFilter) return false;
      if (milestone && t.milestoneId !== milestone) return false;
      if (project === "-" ? (t.projectId ?? null) !== null : project && t.projectId !== project) return false;
      if (needle && !t.title.toLowerCase().includes(needle) && !t.id.includes(needle) && !(t.assignee ?? "").toLowerCase().includes(needle))
        return false;
      return true;
    });
  }, [list, q, milestone, epicFilter, project]);

  /** 「줄 순서」는 **서버가 준 순서를 그대로 쓴다** — 화면이 다시 계산하면 마일스톤 축과
   *  큐 축을 둘 다 아는 서버의 정렬(`TASK_ORDER`)과 갈릴 수 있다. */
  const sorted = useMemo(() => {
    const rows = [...shown];
    // 방향은 목록 보기에서만 뜻이 있다 — 보드의 열 순서는 줄 순서 그대로다.
    const dir = view === "list" && !asc ? -1 : 1;
    // 등록 시각이 없는 줄은 방향과 상관없이 뒤로 — 없는 값을 0 으로 읽지 않는다.
    const created = (t: Issue) => (t.createdAt ? Date.parse(t.createdAt) : null);
    if (sort === "updated") rows.sort((a, b) => dir * (Date.parse(b.updatedAt) - Date.parse(a.updatedAt)));
    else if (sort === "created")
      rows.sort((a, b) => {
        const x = created(a);
        const y = created(b);
        if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
        return dir * (y - x);
      });
    else if (sort === "title") rows.sort((a, b) => dir * a.title.localeCompare(b.title, "ko"));
    else if (sort === "priority")
      rows.sort((a, b) => dir * (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]) || rankOf(a) - rankOf(b));
    else if (dir < 0) rows.reverse();
    return rows;
  }, [shown, sort, asc, view]);
  const openRows = useMemo(() => sorted.filter((t) => !isClosed(t)), [sorted]);
  const closedRows = useMemo(() => sorted.filter(isClosed), [sorted]);

  /** 카드를 옮긴다. **로컬 상태를 먼저 바꾸지 않는다** — 낙관적으로 옮겨 두면 서버가
   *  거절했을 때 되돌리는 길이 하나 더 생기고, 그 되돌리기가 갈릴 수 있다. 대신 요청이
   *  끝나면 항상 `load()` 로 서버 상태를 다시 그린다(성공이든 거절이든 같다).
   *
   *  열 사이 이동은 `POST .../status`, 같은 열 안 순서는 `POST .../queue-order`(이웃 두
   *  id) 다 — 다른 열의 카드 위에 놓으면 **둘 다** 부른다(상태를 옮기고 그 자리에 앉힌다). */
  async function move(id: string, colId: string, beforeId?: string) {
    if (!issues || busy) return;
    const col = COLS.find((c) => c.id === colId)!;
    const src = issues.find((t) => t.id === id);
    if (!src) return;
    const crossCol = !col.has.includes(src.status);

    setBusy(true);
    setActionErr(null);
    try {
      if (crossCol) {
        await tasksApi.setStatus(id, col.drop);
      }
      const inCol = issues.filter((t) => col.has.includes(t.status) && t.id !== id);
      let at = inCol.length;
      if (beforeId) {
        const idx = inCol.findIndex((t) => t.id === beforeId);
        if (idx !== -1) at = idx;
      }
      const prevId = at > 0 ? inCol[at - 1].id : undefined;
      const nextId = at < inCol.length ? inCol[at].id : undefined;
      if (prevId || nextId) {
        await tasksApi.setQueueOrder(id, prevId, nextId);
      }
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      await load();
      setBusy(false);
    }
  }

  /* 사이드바는 **이 화면의 맥락 메뉴**라 이 화면이 자기 상태로 그린다.
     셸에 올려 두면 필터 상태가 화면 밖으로 새어 나간다. */
  const side = (
    <>
      <SideGroup>{tx("board.milestone")}</SideGroup>
      <SideAction icon="layers" active={milestone === ""} onClick={() => setMilestone("")}>
        {tx("board.all")}
      </SideAction>
      {milestones.map((m) => (
        <SideAction key={m.id} icon="flag" active={milestone === m.id} onClick={() => setMilestone(m.id)}>
          {m.title}
        </SideAction>
      ))}
      <SideGroup>{tx("board.project")}</SideGroup>
      <SideAction icon="layers" active={project === ""} onClick={() => setProject("")}>
        {tx("board.all")}
      </SideAction>
      {projects
        ? projects.map((p) => (
            // 숫자는 서버가 센 열린 이슈 수 그대로다(`openTaskCount`) — 화면이 다시 세지 않는다.
            <SideAction key={p.id} icon="hash" active={project === p.id} count={p.openTaskCount} onClick={() => setProject(p.id)}>
              {p.name}
            </SideAction>
          ))
        : null}
      <SideAction icon="hash" active={project === "-"} onClick={() => setProject("-")}>
        {tx("board.projectNone")}
      </SideAction>
      {projects === false ? <SideNote>{tx("board.projectFailed")}</SideNote> : null}
      <SideNote>
        {tx("board.seeing", { shown: sorted.length, total: list.length })}
      </SideNote>
    </>
  );

  // **못 읽었으면 빈 보드를 정상처럼 그리지 않는다** — 「0 건」과 「못 읽었다」는 사람이
  // 할 일이 정반대다. 무엇이(API) · 어디서(주소) · 다음에 뭘(다시 시도) 할지를 말한다.
  if (loadErr) {
    return (
      <AppShell route="/board" sidebarTitle={tx("board.menu")}>
        <div className="board">
          <PageHead title={tx("nav.board")} />
          <Alert tone="error">{tx("board.loadFailed", { err: loadErr })}</Alert>
          <Button size="sm" onClick={load}>
            {tx("common.action.retry")}
          </Button>
        </div>
      </AppShell>
    );
  }

  if (issues === null) {
    return (
      <AppShell route="/board" sidebarTitle={tx("board.menu")}>
        <div className="board">
          <PageHead title={tx("nav.board")} />
          <Loading text={tx("board.loading")} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell route="/board" sidebarTitle={tx("board.menu")} sidebar={side}>
    <div className="board">
      <PageHead title={tx("nav.board")} />

      {actionErr ? (
        <div className="board__actionerr">
          <Alert tone="error">{actionErr}</Alert>
          <Button size="sm" onClick={() => setActionErr(null)}>
            {tx("common.action.close")}
          </Button>
        </div>
      ) : null}
      {missing ? (
        <div className="board__actionerr">
          <Alert tone="warning">
            {rich(tx("board.missing", hole("id")), { id: <code>{missing}</code> })}
          </Alert>
          <Button size="sm" onClick={closeDetail}>
            {tx("common.action.close")}
          </Button>
        </div>
      ) : null}

      <div className="board__bar">
        <div className="b-tabs" role="group" aria-label={tx("board.viewLabel")}>
          <button type="button" className={`b-tab${view === "board" ? " is-on" : ""}`} aria-pressed={view === "board"} onClick={() => setView("board")}>
            {tx("board.viewBoard")}
          </button>
          <button type="button" className={`b-tab${view === "list" ? " is-on" : ""}`} aria-pressed={view === "list"} onClick={() => setView("list")}>
            {tx("board.viewList")}
          </button>
        </div>

        <Input
          size="md"
          className="board__q"
          placeholder={tx("board.searchHint")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label={tx("board.searchLabel")}
        />

        <Select size="md" value={milestone} onChange={(e) => setMilestone(e.target.value)} aria-label={tx("board.milestone")}>
          <option value="">{tx("board.msAll")}</option>
          {milestones.map((m) => (
            <option key={m.id} value={m.id}>
              {m.title}
            </option>
          ))}
        </Select>

        {view === "list" ? (
          <>
            <Select size="md" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label={tx("board.sortLabel")}>
              <option value="queue">{tx("board.sort.queue")}</option>
              <option value="created">{tx("board.sort.created")}</option>
              <option value="updated">{tx("board.sort.updated")}</option>
              <option value="priority">{tx("board.sort.priority")}</option>
              <option value="title">{tx("board.sort.title")}</option>
            </Select>
            <Button size="md" aria-label={tx("board.dir")} onClick={() => setAsc((v) => !v)}>
              {asc ? `↑ ${tx("board.asc")}` : `↓ ${tx("board.desc")}`}
            </Button>
          </>
        ) : null}
      </div>

      {epicFilter ? (
        <div className="board__filterbar">
          <span>
            {rich(tx("board.epicFiltered", hole("title")), { title: <b>{epicTitle(epicFilter)}</b> })}
          </span>
          <Button size="sm" onClick={() => setEpicFilter(null)}>
            {tx("board.unfilter")}
          </Button>
        </div>
      ) : null}

      {sorted.length === 0 ? (
        <EmptyState
          title={tx(list.length === 0 ? "board.empty.none" : "board.empty.noMatch")}
          desc={tx(list.length === 0 ? "board.empty.noneDesc" : "board.empty.noMatchDesc")}
          action={
            list.length === 0 ? undefined : (
              <Button size="md" onClick={() => { setQ(""); setMilestone(""); setEpicFilter(null); setProject(""); }}>
                {tx("board.clear")}
              </Button>
            )
          }
        />
      ) : view === "board" ? (
        <>
        <BoardMeter rows={sorted} />
        <div className="board__cols">
          {COLS.map((col) => {
            const cards = sorted.filter((t) => col.has.includes(t.status));
            const isFolded = folded.has(col.id);
            const card = (t: Issue) => (
              <IssueCard
                key={t.id}
                t={t}
                epicTitle={epicTitle(t.parentId)}
                milestoneLabel={milestoneTitle(t.milestoneId)}
                colStatus={col.drop}
                dragging={drag === t.id}
                draggable
                onDragStart={() => setDrag(t.id)}
                onDragEnd={() => setDrag(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (drag && drag !== t.id) move(drag, col.id, t.id);
                  setDrag(null);
                }}
                selected={picked.has(t.id)}
                onSelect={(on) => togglePick(t.id, on)}
                onOpen={() => setOpenId(t.id)}
                onFilterEpic={t.parentId ? () => setEpicFilter(t.parentId) : undefined}
              />
            );
            return (
              <section
                key={col.id}
                className={`bcol${isFolded ? " is-folded" : ""}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (drag) move(drag, col.id);
                  setDrag(null);
                }}
              >
                <header className="bcol__head">
                  <h2 className="bcol__title">{col.label}</h2>
                  <span className="bcol__count">{cards.length}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon
                    aria-expanded={!isFolded}
                    aria-label={tx(isFolded ? "board.unfold" : "board.fold", { col: col.label })}
                    title={tx(isFolded ? "board.unfold" : "board.fold", { col: col.label })}
                    onClick={() => toggleFold(col.id)}
                  >
                    <Icon name={isFolded ? "chevronRight" : "chevronLeft"} />
                  </Button>
                </header>
                {isFolded ? null : (
                <div className="bcol__body">
                  {cards.length === 0 ? (
                    <p className="bcol__empty">{tx("board.colEmpty")}</p>
                  ) : (
                    groupCol(cards).map((it) => {
                      if (it.kind === "card") return card(it.t);
                      const key = `${col.id}|${it.parentId}`;
                      const isShut = shut.has(key);
                      return (
                        <div key={key} className="bcol__epic">
                          <button
                            type="button"
                            className="bcol__epichead"
                            aria-expanded={!isShut}
                            onClick={() =>
                              setShut((x) => {
                                const n = new Set(x);
                                if (n.has(key)) n.delete(key);
                                else n.add(key);
                                return n;
                              })
                            }
                          >
                            <span aria-hidden="true">{isShut ? "▸" : "▾"}</span>
                            <span className="bcol__epictitle">◇ {epicTitle(it.parentId) ?? it.parentId}</span>
                            <span className="bcol__count">{tx("board.kidsHere", { n: it.kids.length })}</span>
                          </button>
                          {isShut ? null : it.kids.map(card)}
                        </div>
                      );
                    })
                  )}
                </div>
                )}
              </section>
            );
          })}
        </div>
        </>
      ) : (
        <>
          <IssueTable rows={openRows.slice(0, listShown)} picked={picked} onPick={togglePick} onPickAll={(on) => setPicked(on ? new Set(openRows.slice(0, listShown).map((t) => t.id)) : new Set())} onOpen={setOpenId} milestoneTitle={milestoneTitle} selectAll />
          {openRows.length > listShown ? (
            <div className="board__more">
              <Button size="md" onClick={() => setListShown((n) => n + LIST_PAGE)}>
                {tx("board.more", { n: openRows.length - listShown })}
              </Button>
            </div>
          ) : null}
          {closedRows.length ? (
            // 닫힌 줄(완료 · 취소)은 접어 둔다 — **접힌 동안에는 그리지 않는다**(숨긴 행을 DOM 에 두면 선택 · 찾기가 그것을 센다).
            <section className="board__closed">
              <Button size="md" variant="ghost" aria-expanded={closedOpen} onClick={() => setClosedOpen((v) => !v)}>
                {closedOpen ? "▾ " : "▸ "}
                {tx("board.closed", { n: closedRows.length })}
              </Button>
              {closedOpen ? <IssueTable rows={closedRows} picked={picked} onPick={togglePick} onOpen={setOpenId} milestoneTitle={milestoneTitle} /> : null}
            </section>
          ) : null}
        </>
      )}

      {sorted.length ? (
        <BoardSelBar
          // 지워졌거나 걸러져 안 보이는 카드는 고른 목록에서도 뺀다 — 안 보이는 것에 단추가 닿으면 안 된다.
          picked={(view === "list" && !closedOpen ? openRows : sorted).filter((t) => picked.has(t.id))}
          onDone={(ok) => {
            setPicked((p) => new Set([...p].filter((id) => !ok.includes(id))));
            void load();
          }}
          onClear={() => setPicked(new Set())}
        />
      ) : null}

      {open ? (
        // 상세는 **팝업 하나**다(시안 채택) — 화면을 갈아타지 않으니 걸러 둔 조건이 그대로 남는다.
        <IssueDetailModal
          t={open}
          all={list}
          onOpen={(id) => setOpenId(id)}
          epicTitle={epicTitle(open.parentId)}
          milestoneLabel={milestoneTitle(open.milestoneId)}
          onClose={closeDetail}
          onChanged={() => void load()}
          onDeleted={() => {
            closeDetail();
            void load();
          }}
        />
      ) : null}
    </div>
    </AppShell>
  );
}

/** 목록 보기의 표 한 벌 — 열린 줄과 접힌 닫힌 줄이 같은 모양을 쓴다. 고르기 칸은 공통 `.b-check` 다. */
function IssueTable({
  rows,
  picked,
  onPick,
  onPickAll,
  onOpen,
  milestoneTitle,
  selectAll,
}: {
  rows: Issue[];
  picked: Set<string>;
  onPick: (id: string, on: boolean) => void;
  onPickAll?: (on: boolean) => void;
  onOpen: (id: string) => void;
  milestoneTitle: (id: string | null) => string | undefined;
  selectAll?: boolean;
}) {
  const all = rows.length > 0 && rows.every((t) => picked.has(t.id));
  const some = !all && rows.some((t) => picked.has(t.id));
  return (
    <div className="b-table-wrap">
      <table className="b-table itable">
        <thead>
          <tr>
            <th className="itable__pick">
              {selectAll && onPickAll ? (
                <label className="b-check">
                  <input
                    type="checkbox"
                    checked={all}
                    ref={(el) => {
                      if (el) el.indeterminate = some;
                    }}
                    onChange={(e) => onPickAll(e.target.checked)}
                    aria-label={tx("board.pickAll")}
                  />
                </label>
              ) : null}
            </th>
            <th>{tx("field.status")}</th>
            <th>{tx("board.col.title")}</th>
            <th>{tx("board.milestone")}</th>
            <th>{tx("board.col.priority")}</th>
            <th>{tx("board.col.complexity")}</th>
            <th>{tx("board.col.updated")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} tabIndex={0} onClick={() => onOpen(t.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(t.id)}>
              <td className="itable__pick" onClick={(e) => e.stopPropagation()}>
                <label className="b-check">
                  <input type="checkbox" checked={picked.has(t.id)} onChange={(e) => onPick(t.id, e.target.checked)} aria-label={tx("board.pickOne", { title: t.title })} />
                </label>
              </td>
              <td>
                <StatusPill status={t.status} working={isWorking(t)} />
              </td>
              <td className="itable__title">
                {t.childCount > 0 ? <span aria-hidden="true">◇ </span> : null}
                {t.title} <code className="muted">{t.id}</code>
                {/* 표 행은 좁다. 보드 카드의 칩 여섯을 그대로 옮기면 제목이 밀려나므로
                    **가장 행동을 바꾸는 둘만** 남긴다 — 나루가 쥐었나, 병합을 기다리나. */}
                {isHeld(t) ? <Chip tone="accent">◆ {tx("issue.heldShort")}</Chip> : null}
                {isMerging(t) ? <Chip tone="success">⧉ {tx("issue.merging")}</Chip> : null}
              </td>
              <td>{t.milestoneId ? (milestoneTitle(t.milestoneId) ?? t.milestoneId) : "—"}</td>
              <td>{priorityText(t.priority)}</td>
              <td>{complexityText(t.complexity)}</td>
              <td>{tx("issue.ago", { when: minutesSince(t.updatedAt) })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 진행률 — 보이는 이슈 중 완료 비율. 취소는 분모에서 뺀다(「안 하기로 했다」는 남은 일이 아니다).
 *  부모(에픽)는 하위에서 파생되니 잎만 센다 — 대시보드 진행률과 같은 셈. 공통 `.b-meter`. */
function BoardMeter({ rows }: { rows: Issue[] }) {
  const leaves = rows.filter((t) => t.childCount === 0 && t.status !== "cancelled");
  if (!leaves.length) return null;
  const done = leaves.filter((t) => t.status === "done").length;
  const pct = Math.round((done / leaves.length) * 100);
  return (
    <div className="b-meter board__meter" role="group" aria-label={tx("board.progress")}>
      <div className="b-meter-head">
        <span>{tx("board.progress")}</span>
        <span className="tnum">{tx("board.progressVal", { done, total: leaves.length, pct })}</span>
      </div>
      <div className="b-meter-track">
        <div className="b-meter-bar" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
